import React, { useEffect } from 'react';
import { FormProvider, useForm } from '../../contexts/FormContext';
import styles from './MultiStepForm.module.css';

// Komponen untuk menampilkan indikator step
export const StepIndicator = ({ steps, activeStep }) => {
    return (
        <div className={styles.stepIndicator}>
            {steps.map((step, index) => (
                <div 
                    key={index} 
                    className={`${styles.step} ${index === activeStep ? styles.activeStep : ''} ${index < activeStep ? styles.completedStep : ''}`}
                >
                    <div className={styles.stepNumber}>{index + 1}</div>
                    <div className={styles.stepTitle}>{step.title}</div>
                </div>
            ))}
        </div>
    );
};

// Komponen FormStep untuk masing-masing langkah
export const FormStep = ({ id, title, children, onValidate }) => {
    const { activeStep, registerStep, steps } = useForm();
    const stepIndex = steps.indexOf(id);
    
    // Daftarkan step ini saat komponen di-mount
    useEffect(() => {
        registerStep(id);
    }, [id, registerStep]);
    
    // Hanya render jika ini adalah step yang aktif
    if (stepIndex !== activeStep) {
        return null;
    }

    return <div className={styles.formStep}>{children}</div>;
};

// Navigasi MultiStepForm
export const FormNavigation = ({ onPrevious, onNext, onSubmit, isLastStep, isFirstStep, isSubmitting }) => {
    return (
        <div className={styles.formNavigation}>
            {!isFirstStep && (
                <button 
                    type="button" 
                    className={styles.buttonPrev} 
                    onClick={onPrevious}
                    disabled={isSubmitting}
                >
                    Kembali
                </button>
            )}
            <div className={styles.spacer} />
            {!isLastStep ? (
                <button 
                    type="button" 
                    className={styles.buttonNext} 
                    onClick={onNext}
                    disabled={isSubmitting}
                >
                    Lanjutkan
                </button>
            ) : (
                <button 
                    type="button" 
                    className={styles.buttonSubmit} 
                    onClick={onSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Menyimpan...' : 'Simpan Data'}
                </button>
            )}
        </div>
    );
};

// Komponen MultiStepForm utama
const MultiStepForm = ({ children, initialValues = {}, onSubmit }) => {
    const childrenArray = React.Children.toArray(children)
        .filter((child) => React.isValidElement(child) && child.type === FormStep);

    const steps = childrenArray.map(child => ({
        id: child.props.id,
        title: child.props.title,
        onValidate: child.props.onValidate,
    }));

    const handleSubmit = async (formData) => {
        if (onSubmit) {
            return await onSubmit(formData);
        }
    };

    // Render MultiStepForm wrapper dengan FormProvider
    return (
        <FormProvider initialValues={initialValues} onSubmit={handleSubmit}>
            <MultiStepFormContent steps={steps}>
                {children}
            </MultiStepFormContent>
        </FormProvider>
    );
};

// Konten internal MultiStepForm (menggunakan context)
const MultiStepFormContent = ({ children, steps }) => {
    const { 
        activeStep, 
        formData, 
        errors, 
        goToNextStep, 
        goToPrevStep, 
        submitForm, 
        isSubmitting,
        submitStatus, 
        submitMessage 
    } = useForm();
    
    // Callback untuk menangani validasi dan navigasi
    const handleNext = async () => {
        // Validasi step saat ini jika ada fungsi validasi
        const currentStep = steps[activeStep];
        if (currentStep.onValidate) {
            const newErrors = await currentStep.onValidate(formData);
            if (newErrors && Object.keys(newErrors).length > 0) {
                return false;
            }
        }
        
        // Pindah ke step berikutnya
        return goToNextStep();
    };
    
    // Menangani pengiriman form
    const handleSubmit = async () => {
        // Validasi step terakhir
        const lastStep = steps[activeStep];
        if (lastStep.onValidate) {
            const newErrors = await lastStep.onValidate(formData);
            if (newErrors && Object.keys(newErrors).length > 0) {
                return;
            }
        }
        
        // Submit form
        await submitForm();
    };
    
    // Render form
    return (
        <div className={styles.multiStepForm}>
            <StepIndicator steps={steps} activeStep={activeStep} />
            
            <div className={styles.formContent}>
                {children}
                
                {submitStatus && (
                    <div className={`${styles.message} ${styles[submitStatus]}`}>
                        {submitMessage}
                    </div>
                )}
            </div>
            
            <FormNavigation 
                onNext={handleNext}
                onPrevious={goToPrevStep}
                onSubmit={handleSubmit}
                isFirstStep={activeStep === 0}
                isLastStep={activeStep === steps.length - 1}
                isSubmitting={isSubmitting}
            />
        </div>
    );
};

export default MultiStepForm;